import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import citiesData from '../data/cities_with_coordinates.json';

interface City {
  id: number;
  nome: string;
  microrregiao: {
    mesorregiao: {
      UF: {
        sigla: string;
        nome: string;
      };
    };
  };
}

interface CityMultiSelectProps {
  selectedCityIds: number[];
  onChange: (cityIds: number[]) => void;
  placeholder?: string;
}

const CityMultiSelect: React.FC<CityMultiSelectProps> = ({
  selectedCityIds,
  onChange,
  placeholder = 'Buscar cidades...'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const cities = citiesData as City[];

  useEffect(() => {
    // Filtrar cidades com base no termo de busca
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const filtered = cities.filter(city => 
        city.nome.toLowerCase().includes(term) ||
        city.microrregiao.mesorregiao.UF.sigla.toLowerCase().includes(term) ||
        city.microrregiao.mesorregiao.UF.nome.toLowerCase().includes(term)
      ).slice(0, 50); // Limitar a 50 resultados
      setFilteredCities(filtered);
    } else {
      // Mostrar primeiras 50 cidades ordenadas alfabeticamente quando não há busca
      const sorted = [...cities]
        .sort((a, b) => a.nome.localeCompare(b.nome))
        .slice(0, 50);
      setFilteredCities(sorted);
    }
  }, [searchTerm, cities]);

  useEffect(() => {
    // Fechar dropdown ao clicar fora
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleCity = (cityId: number) => {
    if (selectedCityIds.includes(cityId)) {
      onChange(selectedCityIds.filter(id => id !== cityId));
    } else {
      onChange([...selectedCityIds, cityId]);
      setSearchTerm(''); // Limpar busca ao adicionar cidade
      // Focar input após adicionar cidade
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  };

  const handleRemoveCity = (cityId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedCityIds.filter(id => id !== cityId));
  };

  const getSelectedCities = () => {
    return cities.filter(city => selectedCityIds.includes(city.id));
  };

  const getCityLabel = (city: City) => {
    return `${city.nome} - ${city.microrregiao.mesorregiao.UF.sigla}`;
  };

  return (
    <Container ref={containerRef}>
      <InputWrapper onClick={() => setIsOpen(true)}>
        <SelectedChipsContainer>
          {getSelectedCities().map(city => (
            <Chip key={city.id}>
              <span>{getCityLabel(city)}</span>
              <RemoveButton onClick={(e) => handleRemoveCity(city.id, e)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <line x1="18" y1="6" x2="6" y2="18" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="6" y1="6" x2="18" y2="18" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </RemoveButton>
            </Chip>
          ))}
          <SearchInput
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsOpen(true)}
            placeholder={selectedCityIds.length === 0 ? placeholder : ''}
          />
        </SelectedChipsContainer>
        <DropdownIcon isOpen={isOpen}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <polyline points="6 9 12 15 18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </DropdownIcon>
      </InputWrapper>

      {isOpen && (
        <Dropdown>
          {searchTerm.trim() !== '' && filteredCities.length === 0 && (
            <EmptyState>Nenhuma cidade encontrada</EmptyState>
          )}
          {filteredCities.map(city => (
            <DropdownItem
              key={city.id}
              onClick={() => handleToggleCity(city.id)}
              isSelected={selectedCityIds.includes(city.id)}
            >
              <Checkbox isChecked={selectedCityIds.includes(city.id)}>
                {selectedCityIds.includes(city.id) && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white">
                    <polyline points="20 6 9 17 4 12" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </Checkbox>
              <CityInfo>
                <CityName>{city.nome}</CityName>
                <CityState>{city.microrregiao.mesorregiao.UF.nome} ({city.microrregiao.mesorregiao.UF.sigla})</CityState>
              </CityInfo>
            </DropdownItem>
          ))}
        </Dropdown>
      )}

      {selectedCityIds.length > 0 && (
        <CountBadge>
          {selectedCityIds.length} cidade{selectedCityIds.length !== 1 ? 's' : ''} selecionada{selectedCityIds.length !== 1 ? 's' : ''}
        </CountBadge>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
  min-height: 48px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 40px 8px 12px;
  cursor: text;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
  }

  &:focus-within {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const SelectedChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  min-height: 32px;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--accent);
  color: white;
  padding: 4px 8px 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  max-width: 200px;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 10px;
    height: 10px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  font-size: 15px;
  padding: 4px;
  background: transparent;

  &::placeholder {
    color: #94a3b8;
  }
`;

const DropdownIcon = styled.div<{ isOpen: boolean }>`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%) ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s ease;
  color: #64748b;
  pointer-events: none;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  z-index: 10000;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 0 12px 12px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`;

const DropdownItem = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
  background: ${props => props.isSelected ? '#f0f9ff' : 'transparent'};

  &:hover {
    background: ${props => props.isSelected ? '#e0f2fe' : '#f8fafc'};
  }

  &:first-child {
    border-radius: 12px 12px 0 0;
  }

  &:last-child {
    border-radius: 0 0 12px 12px;
  }
`;

const Checkbox = styled.div<{ isChecked: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.isChecked ? 'var(--accent)' : '#cbd5e1'};
  border-radius: 6px;
  background: ${props => props.isChecked ? 'var(--accent)' : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
`;

const CityInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CityName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
`;

const CityState = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const CountBadge = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

export default CityMultiSelect;
